import { useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import { Loader2, Plus, Pencil, Trash2, Search, Star, Upload, X } from 'lucide-react';
import {
  fetchCategories,
  fetchProducts,
  createProduct,
  updateProduct,
  deleteProduct,
  uploadImage,
  API,
} from '@/lib/api';
import { ADMIN_DASH } from '@/constants/testIds';
import { toast } from 'sonner';
import { resolveImage } from '@/lib/resolveImage';
import WarmImageCacheButton from '@/components/admin/WarmImageCacheButton';

const ALL = { slug: 'all', label: 'All' };

export default function ProductsPanel() {
  const [category, setCategory] = useState('all');
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState(null); // null = closed, {} = new, product = edit

  const { data: categoriesData } = useSWR('categories', fetchCategories);
  const categories = useMemo(() => [ALL, ...(categoriesData || [])], [categoriesData]);

  const { data, isLoading, mutate } = useSWR(
    ['admin-products', category],
    ([, c]) => fetchProducts(c),
    { keepPreviousData: true }
  );

  const products = data || [];

  const filtered = useMemo(() => {
    if (!query.trim()) return products;
    const q = query.toLowerCase();
    return products.filter(
      (p) =>
        p.name?.toLowerCase().includes(q) ||
        p.cut?.toLowerCase().includes(q) ||
        p.description?.toLowerCase().includes(q)
    );
  }, [products, query]);

  const onDelete = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? This cannot be undone.`)) return;
    try {
      await deleteProduct(p.id);
      mutate();
      toast.success('Product deleted');
    } catch {
      toast.error('Failed to delete');
    }
  };

  const onSaved = () => {
    setEditing(null);
    mutate();
  };

  return (
    <div data-testid={ADMIN_DASH.productsPanel}>
      <div className="flex items-end justify-between gap-6 flex-wrap mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.32em] text-white/55">
            Catalogue · {products.length} items
          </div>
          <h2 className="font-display font-light text-3xl sm:text-4xl mt-2">
            Products
          </h2>
        </div>
        <button
          type="button"
          onClick={() => setEditing({})}
          data-testid={ADMIN_DASH.productsNew}
          className="inline-flex items-center gap-2 bg-[var(--mw-primary)] hover:bg-[var(--mw-primary)]/90 text-white px-5 py-2.5 text-[11px] uppercase tracking-[0.22em] transition-colors"
        >
          <Plus size={14} /> New product
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <WarmImageCacheButton />
        <span className="text-[10px] uppercase tracking-[0.28em] text-white/40">
          Rebuild WebP thumbnails after adding new product images
        </span>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {categories.map((c) => {
          const isActive = category === c.slug;
          return (
            <button
              key={c.slug}
              data-testid={ADMIN_DASH.productsFilter(c.slug)}
              onClick={() => setCategory(c.slug)}
              className={`text-[11px] uppercase tracking-[0.22em] px-4 py-2.5 border transition-colors ${
                isActive
                  ? 'bg-[var(--mw-primary)] border-[var(--mw-primary)] text-white'
                  : 'border-white/15 text-white/70 hover:border-white hover:text-white'
              }`}
            >
              {c.label}
            </button>
          );
        })}
      </div>

      <div className="relative mb-8 max-w-md">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/35" />
        <input
          placeholder="Search name, cut or description"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="w-full bg-transparent border border-white/15 focus:border-[var(--mw-primary)] outline-none pl-9 pr-4 py-2.5 text-sm text-white transition-colors"
        />
      </div>

      {isLoading && products.length === 0 ? (
        <div className="py-20 flex justify-center">
          <Loader2 className="animate-spin text-white/40" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="border border-white/10 py-20 text-center text-white/50 text-sm uppercase tracking-[0.24em]">
          No products
        </div>
      ) : (
        <div className="border border-white/10">
          {filtered.map((p) => (
            <ProductRow
              key={p.id}
              product={p}
              onEdit={() => setEditing(p)}
              onDelete={() => onDelete(p)}
            />
          ))}
        </div>
      )}

      {editing !== null && (
        <ProductFormModal
          product={editing.id ? editing : null}
          categories={categoriesData || []}
          onClose={() => setEditing(null)}
          onSaved={onSaved}
        />
      )}
    </div>
  );
}

function ProductRow({ product: p, onEdit, onDelete }) {
  return (
    <div
      data-testid={ADMIN_DASH.productRow(p.id)}
      className="grid grid-cols-12 gap-4 items-center px-5 py-3 border-b border-white/10 last:border-b-0 hover:bg-white/[0.03] transition-colors"
    >
      <div className="col-span-12 sm:col-span-1">
        {p.image_url ? (
          <img
            src={resolveImage(p.image_url, 160)}
            alt={p.name}
            className="w-12 h-12 object-cover border border-white/10"
            loading="lazy"
            decoding="async"
            width="48"
            height="48"
          />
        ) : (
          <div className="w-12 h-12 border border-white/10 bg-white/5" />
        )}
      </div>
      <div className="col-span-12 sm:col-span-5">
        <div className="flex items-center gap-2">
          <h4 className="font-display text-lg text-white leading-tight">{p.name}</h4>
          {p.featured && (
            <Star size={12} className="text-[var(--mw-primary)] fill-[var(--mw-primary)]" />
          )}
        </div>
        <div className="text-[10px] uppercase tracking-[0.24em] text-white/45 mt-0.5">
          {p.cut} · {p.grade}
        </div>
      </div>
      <div className="col-span-8 sm:col-span-3 text-sm text-white/55 leading-snug line-clamp-2">
        {p.description}
      </div>
      <div className="col-span-4 sm:col-span-2 text-[10px] uppercase tracking-[0.22em] text-white/45">
        {p.category}
      </div>
      <div className="col-span-12 sm:col-span-1 flex justify-end gap-3">
        <button
          type="button"
          onClick={onEdit}
          data-testid={ADMIN_DASH.productEdit(p.id)}
          className="text-white/55 hover:text-white transition-colors"
          aria-label="Edit"
        >
          <Pencil size={14} />
        </button>
        <button
          type="button"
          onClick={onDelete}
          data-testid={ADMIN_DASH.productDelete(p.id)}
          className="text-white/55 hover:text-[var(--mw-primary)] transition-colors"
          aria-label="Delete"
        >
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
}

function ProductFormModal({ product, categories, onClose, onSaved }) {
  const isEdit = !!product;
  const [form, setForm] = useState({
    category: product?.category || categories[0]?.slug || 'beef',
    name: product?.name || '',
    cut: product?.cut || '',
    description: product?.description || '',
    grade: product?.grade || '',
    origin: product?.origin || 'Australia',
    price: product?.price ?? 0,
    wholesale_price: product?.wholesale_price ?? 0,
    cost_price: product?.cost_price ?? 0,
    export_price: product?.export_price ?? 0,
    unit: product?.unit || 'kg',
    min_qty: product?.min_qty ?? 0.5,
    step_qty: product?.step_qty ?? 0.5,
    image_url: product?.image_url || '',
    tags: product?.tags || [],
    featured: !!product?.featured,
  });
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const onChange = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadImage(file);
      onChange('image_url', res.url);
      toast.success('Image uploaded');
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!form.name.trim()) {
      setError('Name is required');
      return;
    }
    setSubmitting(true);
    try {
      if (isEdit) {
        await updateProduct(product.id, form);
        toast.success('Product updated');
      } else {
        await createProduct(form);
        toast.success('Product created');
      }
      onSaved();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Failed to save');
    } finally {
      setSubmitting(false);
    }
  };

  // Close on ESC
  useEffect(() => {
    const handler = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 sm:p-8 overflow-y-auto">
      <form
        onSubmit={onSubmit}
        data-testid={ADMIN_DASH.productForm}
        className="w-full max-w-2xl bg-[var(--mw-surface)] border border-white/10 my-auto"
      >
        <div className="flex items-center justify-between px-6 sm:px-8 py-5 border-b border-white/10">
          <div>
            <div className="text-[10px] uppercase tracking-[0.32em] text-white/55">
              {isEdit ? 'Edit product' : 'New product'}
            </div>
            <h3 className="font-display text-2xl text-white mt-1">
              {isEdit ? product.name : 'Add to catalogue'}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid={ADMIN_DASH.productFormCancel}
            className="text-white/55 hover:text-white transition-colors"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-6 sm:px-8 py-6 grid sm:grid-cols-2 gap-5">
          <Field label="Category">
            <select
              value={form.category}
              onChange={(e) => onChange('category', e.target.value)}
              data-testid={ADMIN_DASH.productFormCategory}
              className="input"
            >
              {categories.map((c) => (
                <option key={c.slug} value={c.slug}>{c.label}</option>
              ))}
            </select>
          </Field>
          <Field label="Name *">
            <input
              type="text"
              value={form.name}
              onChange={(e) => onChange('name', e.target.value)}
              data-testid={ADMIN_DASH.productFormName}
              className="input"
              required
            />
          </Field>
          <Field label="Cut">
            <input
              type="text"
              value={form.cut}
              onChange={(e) => onChange('cut', e.target.value)}
              data-testid={ADMIN_DASH.productFormCut}
              className="input"
            />
          </Field>
          <Field label="Grade">
            <input
              type="text"
              value={form.grade}
              onChange={(e) => onChange('grade', e.target.value)}
              data-testid={ADMIN_DASH.productFormGrade}
              className="input"
            />
          </Field>
          <Field label="Origin">
            <input
              type="text"
              value={form.origin}
              onChange={(e) => onChange('origin', e.target.value)}
              data-testid={ADMIN_DASH.productFormOrigin}
              className="input"
            />
          </Field>
          <Field label="Retail price (AUD)">
            <input
              type="number"
              step="0.01"
              min="0"
              value={form.price}
              onChange={(e) => onChange('price', parseFloat(e.target.value) || 0)}
              data-testid="admin-product-price"
              className="input"
            />
          </Field>
          <Field label="Wholesale price (AUD)">
            <input
              type="number"
              step="0.01"
              min="0"
              value={form.wholesale_price}
              onChange={(e) => onChange('wholesale_price', parseFloat(e.target.value) || 0)}
              data-testid="admin-product-wholesale-price"
              className="input"
            />
          </Field>
          <Field label="Cost price (AUD)" hint="What we pay for the raw meat">
            <input
              type="number"
              step="0.01"
              min="0"
              value={form.cost_price}
              onChange={(e) => onChange('cost_price', parseFloat(e.target.value) || 0)}
              data-testid="admin-product-cost-price"
              className="input"
            />
          </Field>
          <Field label="Export price (AUD)" hint="International / bulk trade tier">
            <input
              type="number"
              step="0.01"
              min="0"
              value={form.export_price}
              onChange={(e) => onChange('export_price', parseFloat(e.target.value) || 0)}
              data-testid="admin-product-export-price"
              className="input"
            />
          </Field>
          <Field label="Unit">
            <select
              value={form.unit}
              onChange={(e) => onChange('unit', e.target.value)}
              data-testid="admin-product-unit"
              className="input"
            >
              <option value="kg">Per kg</option>
              <option value="each">Per each</option>
              <option value="pack">Per pack</option>
            </select>
          </Field>
          <Field label="Min qty">
            <input
              type="number"
              step="0.1"
              min="0.1"
              value={form.min_qty}
              onChange={(e) => onChange('min_qty', parseFloat(e.target.value) || 0.5)}
              className="input"
            />
          </Field>
          <Field label="Tags (comma-separated)" className="sm:col-span-2">
            <input
              type="text"
              value={(form.tags || []).join(', ')}
              onChange={(e) =>
                onChange(
                  'tags',
                  e.target.value.split(',').map((t) => t.trim()).filter(Boolean)
                )
              }
              className="input"
            />
          </Field>

          <Field label="Description" className="sm:col-span-2">
            <textarea
              value={form.description}
              onChange={(e) => onChange('description', e.target.value)}
              data-testid={ADMIN_DASH.productFormDescription}
              rows={3}
              className="input resize-none"
            />
          </Field>

          <Field label="Image URL" className="sm:col-span-2">
            <input
              type="url"
              value={form.image_url}
              onChange={(e) => onChange('image_url', e.target.value)}
              data-testid={ADMIN_DASH.productFormImageUrl}
              placeholder="https://… or upload below"
              className="input"
            />
          </Field>

          <div className="sm:col-span-2 flex items-center gap-4">
            <label
              data-testid={ADMIN_DASH.productFormImageUpload}
              className="inline-flex items-center gap-2 border border-white/15 hover:border-white text-white px-4 py-2.5 text-[11px] uppercase tracking-[0.22em] cursor-pointer transition-colors"
            >
              {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
              {uploading ? 'Uploading…' : 'Upload image'}
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                onChange={onUpload}
                disabled={uploading}
                className="hidden"
              />
            </label>
            {form.image_url && (
              <img
                src={resolveImage(form.image_url)}
                alt="preview"
                className="w-14 h-14 object-cover border border-white/10"
              />
            )}
          </div>

          <label className="sm:col-span-2 flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={form.featured}
              onChange={(e) => onChange('featured', e.target.checked)}
              data-testid={ADMIN_DASH.productFormFeatured}
              className="accent-[var(--mw-primary)]"
            />
            <span className="text-sm text-white/75">Featured on landing page</span>
          </label>

          {error && (
            <div className="sm:col-span-2 border border-[var(--mw-primary)]/40 text-[var(--mw-primary)] text-xs uppercase tracking-[0.22em] px-4 py-3">
              {typeof error === 'string' ? error : JSON.stringify(error)}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 px-6 sm:px-8 py-5 border-t border-white/10">
          <button
            type="button"
            onClick={onClose}
            className="border border-white/15 hover:border-white text-white px-5 py-2.5 text-[11px] uppercase tracking-[0.22em] transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            data-testid={ADMIN_DASH.productFormSubmit}
            className="bg-[var(--mw-primary)] hover:bg-[var(--mw-primary)]/90 disabled:opacity-60 text-white px-5 py-2.5 text-[11px] uppercase tracking-[0.22em] flex items-center gap-2 transition-colors"
          >
            {submitting && <Loader2 className="animate-spin" size={14} />}
            {isEdit ? 'Save changes' : 'Create product'}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children, hint, className = '' }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-[10px] uppercase tracking-[0.24em] text-white/55 mb-2">
        {label}
      </span>
      {children}
      {hint && (
        <span className="block text-[10px] text-white/40 mt-1">{hint}</span>
      )}
    </label>
  );
}
